/**
 * Formats a unix timestamp (milliseconds or seconds) or ISO date string to a human-readable date
 */
export const formatDate = (dateValue?: string | number, includeTime: boolean = false): string => {
  if (!dateValue) return 'Unknown date';

  try {
    let date: Date;

    // Check if it's a unix timestamp (number or numeric string)
    if (typeof dateValue === 'number') {
      // Determine if it's in seconds or milliseconds
      // Timestamps before year 2001 in milliseconds would be < 1000000000000
      // Timestamps after year 2001 in seconds would be > 1000000000
      // If the number is less than 10000000000, it's likely in seconds
      const timestamp = dateValue < 10000000000 ? dateValue * 1000 : dateValue;
      date = new Date(timestamp);
    } else if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
      // Numeric string, determine if in seconds or milliseconds
      const numValue = parseInt(dateValue);
      const timestamp = numValue < 10000000000 ? numValue * 1000 : numValue;
      date = new Date(timestamp);
    } else {
      // ISO string or other date format
      date = new Date(dateValue);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(dateValue);
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };

    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }

    return date.toLocaleDateString('en-US', options);
  } catch {
    return String(dateValue);
  }
};

/**
 * Formats a unix timestamp (milliseconds) to a full date time string
 */
export const formatDateTime = (timestamp?: string | number): string => {
  return formatDate(timestamp, true);
};

/**
 * Formats a timestamp for logs with more detail
 */
export const formatLogTimestamp = (timestamp: number): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(timestamp);
  }
};
